import { DESIGN_MODE_CUSTOM_CSS_SCRIPT_SELECTOR } from "@/constants";
import { WISHLIST_WIDGET_NAMES } from "@/constants/widget-names";
import { growaveConfigurationToken } from "@/tokens";
import { isShadowRoot } from "@/utils/isShadowRoot/isShadowRoot";
import { WishlistProductStatusesQuery } from "@apps/wishlist/queries/WishlistProductStatusesQuery";
import { WishlistProductStatusesService } from "@apps/wishlist/services/WishlistProductStatusesService";
import { WishlistsService } from "@apps/wishlist/services/WishlistsService";
import WishlistsStorefrontValuesService from "@apps/wishlist/services/WishlistsStorefrontValuesService";
import CustomizationManager from "@lib/CustomizationManager/CustomizationManager";
import { ProductIdManagerInterface } from "@lib/ProductIdManager/ProductIdManagerInterface";
import { ProductVariantIdManagerInterface } from "@lib/ProductVariantIdManager/ProductVariantIdManagerInterface";
import { AuthenticationModule } from "@modules/authentication/AuthenticationModule";
import { DynamicImportModule } from "@modules/dynamic_import/DynamicImportModule";
import { I18nModule } from "@modules/i18n/I18nModule";
import ShopifyAnalyticsManager from "@modules/shopify_analytics/ShopifyAnalyticsManager";
import ShopifyAnalyticsModule from "@modules/shopify_analytics/ShopifyAnalyticsModule";
import { container } from "tsyringe";

import { ModalManager } from "@components/Modal/ModalManager";
import { LoggerInterface } from "@interfaces/LoggerInterface";
import { WidgetInterface } from "@interfaces/WidgetInterface";

import AddToWishlistProductCardWidgetComponent from "./AddToWishlistProductCardWidget.svelte";
import { AddToWishlistProductCardWidgetConfigInterface } from "./AddToWishlistProductCardWidgetConfig";

class AddToWishlistProductCardWidget implements WidgetInterface {
    private readonly shopifyAnalyticsManager: ShopifyAnalyticsManager | null;

    constructor(
        private readonly config: AddToWishlistProductCardWidgetConfigInterface,
        private readonly logger: LoggerInterface,
        private readonly modalManager: ModalManager,
        private readonly productIdManager: ProductIdManagerInterface,
        private readonly productVariantIdManager: ProductVariantIdManagerInterface
    ) {
        this.logger.debug(
            "AddToWishlistProductCardWidget is initialized",
            config
        );

        // I18n
        const i18nModule = container.resolve(I18nModule);
        i18nModule.exports.i18n.registerLoader(
            "wishlist.AddToWishlistProductCardWidget",
            i18nModule.exports.i18nApiLoaderFactory.getLoader(
                "wishlist.AddToWishlistProductCardWidget"
            )
        );
        i18nModule.exports.i18n.registerLoader(
            "wishlist.critical.AddToWishlistProductCardWidget",
            i18nModule.exports.i18nCriticalLoaderFactory.getLoader(
                "wishlist.critical.AddToWishlistProductCardWidget"
            )
        );

        this.shopifyAnalyticsManager = container.resolve(
            ShopifyAnalyticsModule
        ).exports.shopifyAnalyticsManager;
    }

    async init(): Promise<this> {
        const customizationManager = container.resolve(CustomizationManager);
        const authenticationInfoProvider =
            container.resolve(AuthenticationModule).exports
                .authenticationInfoProvider;

        const wishlistProductStatusesService = container.resolve(
            WishlistProductStatusesService
        );
        const wishlistProductStatusesQuery = container.resolve(
            WishlistProductStatusesQuery
        );
        const wishlistsStorefrontValuesService = container.resolve(
            WishlistsStorefrontValuesService
        );
        const wishlistsService = container.resolve(WishlistsService);
        wishlistProductStatusesService.subscribe();

        const dynamicImportManager =
            container.resolve(DynamicImportModule).exports.dynamicImportManager;
        const growaveConfiguration = container.resolve(
            growaveConfigurationToken
        );

        const element = this.config.target as HTMLElement;
        const shadowRoot = isShadowRoot(
            this.config.isWidgetShadowRootEnabled,
            growaveConfiguration.isShadowRootDisabledGlobally
        )
            ? element.shadowRoot || element.attachShadow({ mode: "open" })
            : null;

        const customizationManagerConfig = {
            widgetName:
                WISHLIST_WIDGET_NAMES.ADD_TO_WISHLIST_PRODUCT_CARD_WIDGET,
            placeholder: element,
            shadowRoot,
        };

        customizationManager.injectStylesFromDesignMode({
            element,
            shadowRoot,
            scriptSelector: DESIGN_MODE_CUSTOM_CSS_SCRIPT_SELECTOR,
        });
        customizationManager.injectStylesFromCustomization(
            customizationManagerConfig
        );
        await customizationManager.beforeWidgetInject(
            customizationManagerConfig
        );

        new AddToWishlistProductCardWidgetComponent({
            target: shadowRoot || element,
            props: {
                dynamicImportManager,
                wishlistsService,
                modalManager: this.modalManager,
                authenticationInfoProvider,
                wishlistsStorefrontValuesService,
                wishlistProductStatusesQuery,
                wishlistProductStatusesService,
                productIdManager: this.productIdManager,
                productVariantIdManager: this.productVariantIdManager,
                shopifyAnalyticsManager: this.shopifyAnalyticsManager,
                wishlistCounterPosition:
                    this.config.wishlistCounterPosition || "right-top",
                afterWidgetInject: () => {
                    customizationManager.afterWidgetInject(
                        customizationManagerConfig
                    );
                },
            },
        });

        return this;
    }
}

export default AddToWishlistProductCardWidget;
